import React, { useState, useEffect } from "react";
import { XMarkIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { UseWatchlistCreatorReturn } from "../hooks/useWatchlistCreator";
import { isPrebuiltWatchlist } from "../prebuiltWatchlists";
import { groupWatchlistsByCategory, NEWSQUAWK_CATEGORIES, type NewsquawkCategory } from "../newsquawkCategories";
import { containsTickerDelimiter, containsKeywordDelimiter, parsePastedTickers, parsePastedKeywords } from "../parsePastedList";
import { useTagSearch } from "@/shared/api/newsquawk-headlines";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { useEnsureWatchlistAlerts } from "@/shared/hooks/useEnsureWatchlistAlerts";
import NotificationMethodCheckboxes from "@/ui-components/NotificationMethodCheckboxes";

interface WatchlistBuilderPaneProps {
  creator: UseWatchlistCreatorReturn;
  onCancel: () => void;
}

export default function WatchlistBuilderPane({ creator, onCancel }: WatchlistBuilderPaneProps) {
  const { ensurePopup, ensureSound } = useEnsureWatchlistAlerts();
  const [tickerInput, setTickerInput] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  // Mobile detection for showing full import section vs summary chips
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Resolve imported watchlist names for summary chips
  const importedWatchlists = (creator.mode === "ticker" || creator.mode === "keyword")
    ? creator.portfolios.filter((p) => creator.importedWatchlistIds.has(p.id))
    : [];

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Header — name input only */}
      <div className="px-4 py-3 border-b border-gray-700/30">
        <input
          type="text"
          value={creator.name}
          onChange={(e) => creator.setName(e.target.value)}
          placeholder={`${creator.mode === "tag" ? "Tag watchlist" : "Watchlist"} name`}
          className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
        />
        {creator.errors.name && <p className="text-xs text-red-400 mt-1">{creator.errors.name}</p>}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4 space-y-4">
        {/* Clear all — shown when form has content */}
        {(creator.tickers.length > 0 || creator.keywords.length > 0 || creator.selectedTags.length > 0 || creator.importedWatchlistIds.size > 0) && (
          <div className="flex justify-end">
            <button onClick={creator.clearAll} className="text-xs text-gray-500 hover:text-red-400 transition-colors">
              Clear all
            </button>
          </div>
        )}

        {/* Import: Mobile gets full browser, desktop gets summary chips */}
        {(creator.mode === "ticker" || creator.mode === "keyword") && (
          isMobile ? (
            <NewsquawkImportSection creator={creator} />
          ) : importedWatchlists.length > 0 ? (
            <div>
              <label className="text-2xs font-medium text-gray-500 uppercase tracking-wider">Imported from Newsquawk</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {importedWatchlists.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded bg-squawk-blue/10 border border-squawk-blue/20 text-xs text-squawk-blue"
                  >
                    <span className="truncate max-w-[160px]">{p.name}</span>
                    <button
                      onClick={() => creator.toggleWatchlistImport(p.id)}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <XMarkIcon className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : null
        )}

        {/* Tickers section */}
        {creator.mode === "ticker" && (
          <div>
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Tickers</label>
            <div className="mt-1 flex gap-1.5">
              <input
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); creator.addTicker(tickerInput); setTickerInput(""); } }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!containsTickerDelimiter(text)) return;
                  e.preventDefault();
                  creator.addTickers(parsePastedTickers(text));
                  setTickerInput("");
                }}
                placeholder="Add ticker..."
                className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
              />
              <button onClick={() => { creator.addTicker(tickerInput); setTickerInput(""); }} className="px-2 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
                <PlusIcon className="size-3.5" />
              </button>
            </div>
            <label className="mt-1.5 flex items-center gap-1.5 text-xs text-squawk-blue cursor-pointer hover:text-squawk-blue/80 transition-colors">
              <input type="file" accept=".csv" onChange={(e) => { if (e.target.files?.[0]) { creator.handleCsvUpload(e.target.files[0]); e.target.value = ""; } }} className="sr-only" />
              Upload CSV
            </label>
            {creator.tickers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1 max-h-48 overflow-y-auto scrollbar-thin">
                {creator.tickers.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-blue-900/20 text-blue-300 border border-blue-800/30 text-xs">
                    {t}
                    <button onClick={() => creator.removeTicker(t)} className="p-0.5 text-blue-400 hover:text-blue-200">
                      <XMarkIcon className="size-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {creator.errors.tickers && <p className="text-xs text-red-400 mt-1">{creator.errors.tickers}</p>}
          </div>
        )}

        {/* Keywords section */}
        {creator.mode === "keyword" && (
          <div>
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Keywords</label>
            <div className="mt-1 flex gap-1.5">
              <input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); creator.addKeyword(keywordInput); setKeywordInput(""); } }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!containsKeywordDelimiter(text)) return;
                  e.preventDefault();
                  creator.addKeywords(parsePastedKeywords(text));
                  setKeywordInput("");
                }}
                placeholder="Add keyword..."
                className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
              />
              <button onClick={() => { creator.addKeyword(keywordInput); setKeywordInput(""); }} className="px-2 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
                <PlusIcon className="size-3.5" />
              </button>
            </div>
            <label className="mt-1.5 flex items-center gap-1.5 text-xs text-squawk-blue cursor-pointer hover:text-squawk-blue/80 transition-colors">
              <input type="file" accept=".csv" onChange={(e) => { if (e.target.files?.[0]) { creator.handleKeywordCsvUpload(e.target.files[0]); e.target.value = ""; } }} className="sr-only" />
              Upload CSV
            </label>
            {creator.keywords.length > 0 && (
              <>
                <div className="mt-2 flex flex-wrap gap-1 max-h-48 overflow-y-auto scrollbar-thin">
                  {creator.keywords.map((k) => (
                    <span key={k} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-purple-900/20 text-purple-300 border border-purple-800/30 text-xs">
                      {k}
                      <button onClick={() => creator.removeKeyword(k)} className="p-0.5 text-purple-400 hover:text-purple-200">
                        <XMarkIcon className="size-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className={clsx("mt-1 text-xs", creator.keywordsSize.ok ? "text-gray-500" : "text-red-400")}>
                  {creator.keywordsSize.count}/{creator.MAX_KEYWORDS_COUNT} keywords · {creator.keywordsSize.totalChars}/{creator.MAX_KEYWORDS_TOTAL_CHARS} chars
                </div>
              </>
            )}
            {creator.errors.keywords && <p className="text-xs text-red-400 mt-1">{creator.errors.keywords}</p>}
          </div>
        )}

        {/* Tags section */}
        {creator.mode === "tag" && (
          <TagSelector creator={creator} isMobile={isMobile} />
        )}

        {/* Color */}
        {creator.mode !== "tag" && (
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Color</span>
            <div className="flex items-center gap-2">
              <label className="relative cursor-pointer">
                <span className="block size-7 rounded border border-gray-600 hover:border-squawk-blue transition-colors" style={{ backgroundColor: creator.color }} />
                <input type="color" value={creator.color} onChange={(e) => creator.setColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer size-7" />
              </label>
              <input
                type="text"
                value={creator.color}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(v)) creator.setColor(v);
                }}
                maxLength={7}
                className="w-20 px-2 py-1 text-xs font-mono bg-gray-800 text-gray-300 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
              />
            </div>
          </div>
        )}

        {/* Priority */}
        {creator.mode !== "tag" && (
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Priority</span>
              <p className="text-xs text-gray-600">Lower wins on overlap</p>
            </div>
            <select
              value={creator.priority}
              onChange={(e) => creator.setPriority(Number(e.target.value))}
              className="w-16 px-2 py-1.5 text-sm rounded border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:border-squawk-blue"
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}

        {/* Notifications */}
        <div>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Notifications</span>
          <div className="mt-2">
            <NotificationMethodCheckboxes
              value={creator.notifications}
              onChange={creator.setNotifications}
              comingSoonMethods={creator.mode === "tag" ? ["sound", "popup"] : undefined}
              onEnableMethod={(method) => {
                if (method === "sound") ensureSound();
                if (method === "popup") ensurePopup();
              }}
              variant="pane"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={clsx(
        "px-4 py-3 border-t border-gray-700/30 flex flex-col sm:flex-row sm:items-center gap-2",
        creator.isEditing ? "sm:justify-between" : "sm:justify-end"
      )}>
        {creator.isEditing && (
          <button
            onClick={creator.handleDelete}
            disabled={creator.isSubmitting}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded border border-red-800/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors disabled:opacity-50 order-3 sm:order-1"
          >
            Delete
          </button>
        )}
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2">
          <button
            onClick={onCancel}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={creator.handleSubmit}
            disabled={creator.isSubmitting}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creator.isSubmitting
              ? (creator.isEditing ? "Saving..." : "Creating...")
              : creator.isEditing
                ? "Save Changes"
                : `Create ${creator.mode === "tag" ? "Tag Watchlist" : "Watchlist"}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tag Selector (search + browse by category) ──────────────────────────

function TagSelector({ creator, isMobile }: { creator: UseWatchlistCreatorReturn; isMobile: boolean }) {
  const { search: searchTags, tags: searchedTags, isLoading: tagSearchLoading } = useTagSearch();
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagSearchedQuery, setTagSearchedQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(menuCategories.map((c) => c.title)));

  return (
    <div className="space-y-4">
      {/* Selected tags */}
      {creator.selectedTags.length > 0 && (
        <div>
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Selected ({creator.selectedTags.length})</label>
          <div className="mt-1 flex flex-wrap gap-1 max-h-24 overflow-y-auto scrollbar-thin">
            {creator.selectedTags.map((tag) => (
              <span key={tag.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-green-900/20 text-green-300 border border-green-800/30 text-xs">
                {tag.title}
                <button onClick={() => creator.toggleTag(tag)} className="p-0.5 text-green-400 hover:text-green-200">
                  <XMarkIcon className="size-2.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
      {creator.errors.tags && <p className="text-xs text-red-400">{creator.errors.tags}</p>}

      {/* Search */}
      <div>
        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Search for additional tags</label>
        <div className="mt-1 flex gap-1.5">
          <input
            value={tagSearchQuery}
            onChange={(e) => setTagSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && tagSearchQuery.trim()) { e.preventDefault(); setTagSearchedQuery(tagSearchQuery); searchTags(tagSearchQuery); } }}
            placeholder="Search for tags..."
            className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
          />
          <button
            onClick={() => { if (tagSearchQuery.trim()) { setTagSearchedQuery(tagSearchQuery); searchTags(tagSearchQuery); } }}
            disabled={!tagSearchQuery.trim() || tagSearchLoading}
            className="px-3 py-1.5 text-xs rounded border border-squawk-blue bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors disabled:opacity-50"
          >
            {tagSearchLoading ? "..." : "Search"}
          </button>
        </div>
        {tagSearchedQuery && tagSearchQuery === tagSearchedQuery && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {searchedTags.length === 0 && !tagSearchLoading && <span className="text-xs text-gray-500">No tags found</span>}
            {searchedTags.map((tag) => {
              const isSelected = creator.selectedTags.some((t) => t.id === tag.tag_id);
              return (
                <button
                  key={tag.tag_id}
                  onClick={() => creator.toggleTag({ title: tag.name, id: tag.tag_id })}
                  className={clsx(
                    "px-2 py-1 text-xs rounded border transition-colors",
                    isSelected ? "bg-squawk-blue/20 text-squawk-blue border-squawk-blue" : "bg-gray-700 text-gray-400 border-gray-600 hover:border-gray-500"
                  )}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Browse by category — mobile only (desktop uses center panel) */}
      {isMobile && <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Browse by Category</label>
          <button
            onClick={() => {
              if (collapsedSections.size === menuCategories.length) setCollapsedSections(new Set());
              else setCollapsedSections(new Set(menuCategories.map((c) => c.title)));
            }}
            className="text-2xs px-1.5 py-0.5 rounded border border-gray-700 text-squawk-blue hover:border-squawk-blue/50"
          >
            {collapsedSections.size === menuCategories.length ? "Expand" : "Collapse"}
          </button>
        </div>
        <div className="space-y-1">
          {menuCategories.map((category) => {
            const isCollapsed = collapsedSections.has(category.title);
            return (
              <div key={category.title} className="border border-gray-700/50 rounded overflow-hidden">
                <button
                  onClick={() => setCollapsedSections((prev) => { const next = new Set(prev); if (next.has(category.title)) next.delete(category.title); else next.add(category.title); return next; })}
                  className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-800/30 hover:bg-gray-800/50 transition-colors focus:outline-none"
                >
                  {isCollapsed ? <ChevronRightIcon className="size-3" /> : <ChevronDownIcon className="size-3" />}
                  {category.title}
                </button>
                {!isCollapsed && (
                  <div className="p-2 grid grid-cols-2 gap-1 bg-gray-900/20">
                    {category.items.map((catItem) => {
                      const isSelected = creator.selectedTags.some((t) => t.id === catItem.id);
                      return (
                        <label key={catItem.id} className={clsx(
                          "flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer text-xs transition-colors",
                          isSelected ? "bg-squawk-blue/10 text-squawk-blue" : "text-gray-400 hover:bg-gray-800"
                        )}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => creator.toggleTag({ title: catItem.title, id: catItem.id })}
                            className="size-3 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0"
                          />
                          <span className="truncate">{catItem.title}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>}
    </div>
  );
}

// ── Newsquawk Import Section (for ticker/keyword modes) ────────────────

function NewsquawkImportSection({ creator }: { creator: UseWatchlistCreatorReturn }) {
  const [isOpen, setIsOpen] = useState(false);

  const newsquawkWatchlists = creator.portfolios.filter((p) => {
    if (!isPrebuiltWatchlist(p)) return false;
    if (creator.mode === "ticker") return p.tickers.length > 0;
    return (p.keywords?.length ?? 0) > 0;
  });

  const grouped = groupWatchlistsByCategory(newsquawkWatchlists);

  if (newsquawkWatchlists.length === 0) return null;

  return (
    <div className="border border-gray-700/50 rounded overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left bg-gray-800/50 hover:bg-gray-800 transition-colors"
      >
        <ChevronRightIcon className={clsx("size-3 text-gray-500 transition-transform", isOpen && "rotate-90")} />
        <BookOpenIcon className="size-3.5 text-squawk-blue/60" />
        <span className="text-xs font-medium text-gray-400">Import from Newsquawk</span>
        {creator.importedWatchlistIds.size > 0 && (
          <span className="ml-auto text-2xs px-1.5 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue">
            {creator.importedWatchlistIds.size}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="max-h-48 overflow-y-auto border-t border-gray-700/30 scrollbar-thin scrollbar-thumb-gray-700">
          {NEWSQUAWK_CATEGORIES.map((cat) => {
            const catWatchlists = grouped[cat.id as NewsquawkCategory];
            if (!catWatchlists || catWatchlists.length === 0) return null;
            return (
              <div key={cat.id}>
                <div className="px-3 py-1 text-2xs font-semibold uppercase tracking-wider text-gray-500 bg-gray-800/30">
                  {cat.label}
                </div>
                {catWatchlists.map((portfolio) => {
                  const isImported = creator.importedWatchlistIds.has(portfolio.id);
                  return (
                    <label
                      key={portfolio.id}
                      className={clsx(
                        "flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs transition-colors",
                        isImported ? "bg-squawk-blue/5 text-gray-200" : "text-gray-400 hover:bg-gray-800/50"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isImported}
                        onChange={() => creator.toggleWatchlistImport(portfolio.id)}
                        className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0"
                      />
                      <span className="flex-1 truncate">{portfolio.name}</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
